/**
 * Local-first storage for user-authored worldviews. Lives in browser localStorage
 * under a single JSON-encoded array; this module is the only place that reads or
 * writes that key, so the on-disk shape can evolve in one spot.
 *
 * Stored at the same origin as the app — so dev and prod have separate libraries,
 * and clearing site data clears everything. By design for v1: a worldview is
 * intimate, so it doesn't leave the user's device until they explicitly publish.
 *
 * Shape (v2): each worldview holds a list of versions, oldest first. A revision
 * is created when the user provides feedback on a chat response; the revision
 * carries the feedback that produced it. The original paste is always v1.
 */

import type { ChatSettings, OnboardingAnswer } from '@arena/shared';

export const DEFAULT_CHAT_SETTINGS: ChatSettings = {
  responseLength: 'medium',
  vocabLevel: 'everyday',
};

const STORAGE_KEY = 'arena.worldviews';

export interface VersionFeedback {
  /** The user's question that led to the response being refined. */
  question: string;
  /** The agent's response that the user wanted to refine. */
  response: string;
  /** The user's note describing what would be closer to the truth. */
  note: string;
}

export interface WorldviewVersion {
  /** Monotonic, starting at 1. */
  version: number;
  content: string;
  /** ISO timestamp. */
  createdAt: string;
  /** Present on revisions created from feedback. Absent on v1 (the original). */
  feedback?: VersionFeedback;
}

export interface SavedWorldview {
  id: string;
  /** Optional display name set by the user. Falls back to the first heading in the content. */
  name?: string;
  versions: WorldviewVersion[];
  createdAt: string;
  updatedAt: string;
  /** Per-worldview chat style preferences. Defaults to DEFAULT_CHAT_SETTINGS when absent. */
  settings?: ChatSettings;
  /**
   * Raw answers from the onboarding question step. Stored so the patterns can
   * be re-extracted or audited later; NEVER injected into chat prompts (the
   * abstracted patterns in the markdown content are what reaches the model).
   */
  rawAnswers?: OnboardingAnswer[];
}

// ─── Internal ──────────────────────────────────────────────────────────────

interface RawV1 {
  id: string;
  content: string;
  createdAt: string;
}

function isV2(raw: unknown): raw is SavedWorldview {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    Array.isArray((raw as { versions?: unknown }).versions)
  );
}

function isV1(raw: unknown): raw is RawV1 {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as { content?: unknown }).content === 'string' &&
    typeof (raw as { id?: unknown }).id === 'string'
  );
}

/** Lift an older record shape into the current shape; drop if unrecognizable. */
function migrate(raw: unknown): SavedWorldview | null {
  if (isV2(raw)) return raw;
  if (isV1(raw)) {
    return {
      id: raw.id,
      createdAt: raw.createdAt,
      updatedAt: raw.createdAt,
      versions: [
        {
          version: 1,
          content: raw.content,
          createdAt: raw.createdAt,
        },
      ],
    };
  }
  return null;
}

function readAll(): SavedWorldview[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(migrate).filter((x): x is SavedWorldview => x !== null);
  } catch {
    return [];
  }
}

function writeAll(items: SavedWorldview[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // quota exceeded or storage unavailable; swallow rather than crash the UI
  }
}

// ─── Public ────────────────────────────────────────────────────────────────

/** All saved worldviews, most recently updated first. */
export function listWorldviews(): SavedWorldview[] {
  return readAll().sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function getWorldview(id: string): SavedWorldview | undefined {
  return readAll().find((w) => w.id === id);
}

/** The newest version on a worldview. */
export function getCurrentVersion(wv: SavedWorldview): WorldviewVersion {
  return [...wv.versions].sort((a, b) => b.version - a.version)[0]!;
}

/** Convenience: the markdown content of the newest version. */
export function getCurrentContent(wv: SavedWorldview): string {
  return getCurrentVersion(wv).content;
}

/** Save a brand-new worldview as version 1. Optionally records raw onboarding answers. */
export function saveWorldview(
  content: string,
  rawAnswers?: OnboardingAnswer[],
): SavedWorldview {
  const now = new Date().toISOString();
  const wv: SavedWorldview = {
    id: `wv_${Date.now()}`,
    createdAt: now,
    updatedAt: now,
    versions: [
      {
        version: 1,
        content: content.trim(),
        createdAt: now,
      },
    ],
    ...(rawAnswers && rawAnswers.length > 0 ? { rawAnswers } : {}),
  };
  const all = readAll();
  all.push(wv);
  writeAll(all);
  return wv;
}

/** Append a new version to an existing worldview. Returns the updated record. */
export function addRevision(
  id: string,
  content: string,
  feedback: VersionFeedback,
): SavedWorldview | undefined {
  const all = readAll();
  const idx = all.findIndex((w) => w.id === id);
  if (idx < 0) return undefined;
  const current = all[idx]!;
  const nextVersion = Math.max(0, ...current.versions.map((v) => v.version)) + 1;
  const now = new Date().toISOString();
  const updated: SavedWorldview = {
    ...current,
    updatedAt: now,
    versions: [
      ...current.versions,
      {
        version: nextVersion,
        content: content.trim(),
        createdAt: now,
        feedback,
      },
    ],
  };
  all[idx] = updated;
  writeAll(all);
  return updated;
}

/** Permanently remove a worldview and all its versions. */
export function deleteWorldview(id: string): void {
  writeAll(readAll().filter((w) => w.id !== id));
}

/** Persist chat style settings on a worldview. Returns the updated record. */
export function updateWorldviewSettings(
  id: string,
  settings: ChatSettings,
): SavedWorldview | undefined {
  const all = readAll();
  const idx = all.findIndex((w) => w.id === id);
  if (idx < 0) return undefined;
  const updated: SavedWorldview = { ...all[idx]!, settings };
  all[idx] = updated;
  writeAll(all);
  return updated;
}

/** Set a custom display name on a worldview. Pass an empty string to clear it. Returns the updated record. */
export function renameWorldview(
  id: string,
  name: string,
): SavedWorldview | undefined {
  const all = readAll();
  const idx = all.findIndex((w) => w.id === id);
  if (idx < 0) return undefined;
  const trimmed = name.trim();
  const updated: SavedWorldview = {
    ...all[idx]!,
    name: trimmed || undefined,
  };
  all[idx] = updated;
  writeAll(all);
  return updated;
}
